// Claim lines vs BOQ: typed, pasted from Excel, carried from the last claim, or drafted by Claude. SWI % edited in the table.
import { useState } from 'react';
import { write } from '../../lib/api';
import { lineAmounts, money } from '../../lib/claimCalc';
import type { ClaimLine } from '../../lib/claimData';
import { Button, errText, useToast } from '../kit';

const num = (s: string): number | null => {
  const t = s.replace(/[^0-9.\-()]/g, '');
  if (!t) return null;
  const n = Number(t.replace(/[()]/g, ''));
  return Number.isFinite(n) ? (/^\(.*\)$/.test(t) ? -n : n) : null;
};

/** Excel cells (tab-separated) → lines. Columns: [code,] description, contract value, previous %, claimed % (header row skipped). */
export function parsePaste(text: string): Partial<ClaimLine>[] {
  const rows = text
    .split(/\r?\n/)
    .map((r) => r.split('\t').map((c) => c.trim()))
    .filter((r) => r.some(Boolean));
  const out: Partial<ClaimLine>[] = [];
  for (const r of rows) {
    const cells = [...r];
    while (cells.length && !cells[cells.length - 1]) cells.pop();
    if (cells.length < 4) continue;
    const hasCode = cells.length >= 5;
    const [code, desc, v, p, c] = hasCode ? cells : ['', ...cells];
    const value = num(v);
    if (value === null && out.length === 0) continue; // header
    out.push({ code: code || null, description: desc ?? '', contract_value: value, prev_pct: num(p), claimed_pct: num(c), swi_pct: num(c) });
  }
  return out;
}

export function Lines({
  itemId,
  lines,
  canEdit,
  previousLines,
  onChanged,
}: {
  itemId: string;
  lines: ClaimLine[];
  canEdit: boolean;
  previousLines: { label: string; lines: ClaimLine[] } | null;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [onlyDiff, setOnlyDiff] = useState(false);
  const [paste, setPaste] = useState<string | null>(null);
  const [replace, setReplace] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});

  const rows = lines.map((l) => ({ l, a: lineAmounts(l) }));
  const shown = onlyDiff ? rows.filter((r) => r.a.claimed !== r.a.swi) : rows;
  const tc = rows.reduce((s, r) => s + r.a.claimed, 0);
  const ts = rows.reduce((s, r) => s + r.a.swi, 0);

  async function save(l: ClaimLine, key: keyof ClaimLine, raw: string) {
    const v = ['code', 'description', 'note'].includes(key) ? raw.trim() || null : num(raw);
    if ((l[key] ?? null) === v) return;
    if (key !== 'description' && key !== 'code' && key !== 'note' && typeof v === 'number' && ['prev_pct', 'claimed_pct', 'swi_pct'].includes(key) && (v < 0 || v > 100)) {
      toast({ tone: 'error', text: 'A % must be between 0 and 100' });
      return;
    }
    try {
      await write('claim_lines', 'update', { id: l.id, values: { [key]: key === 'description' ? (v ?? '') : v }, summary: `Line ${l.code ?? ''}: ${key.replace('_', ' ')} → ${raw}` });
      onChanged();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    }
  }
  async function addMany(list: Partial<ClaimLine>[], source: string, summary: string) {
    const batch = crypto.randomUUID();
    setBusy(`Adding ${list.length} lines…`);
    try {
      if (replace) for (const l of lines) await write('claim_lines', 'delete', { id: l.id, batch_id: batch, summary: 'Replaced claim lines' });
      let sort = replace ? 0 : Math.max(0, ...lines.map((l) => l.sort)) + 1;
      for (const l of list) {
        await write('claim_lines', 'insert', { batch_id: batch, values: { item_id: itemId, sort: sort++, source, code: l.code ?? null, description: l.description ?? '', contract_value: l.contract_value ?? null, prev_pct: l.prev_pct ?? null, claimed_pct: l.claimed_pct ?? null, swi_pct: l.swi_pct ?? l.claimed_pct ?? null }, summary });
      }
      toast({ tone: 'ok', text: `${list.length} lines added` });
      setPaste(null);
      onChanged();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    } finally {
      setBusy(null);
    }
  }
  async function remove(l: ClaimLine) {
    if (!window.confirm(`Remove line ${l.code ?? ''} ${l.description}?`)) return;
    try {
      await write('claim_lines', 'delete', { id: l.id, summary: `Removed line ${l.code ?? ''}` });
      onChanged();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    }
  }
  const cell = (l: ClaimLine, key: keyof ClaimLine, cls = '', w?: number) => {
    const k = `${l.id}:${key}`;
    const raw = l[key] === null || l[key] === undefined ? '' : String(l[key]);
    const val = draft[k] ?? (key === 'contract_value' && l.contract_value !== null ? money(l.contract_value) : raw);
    if (!canEdit) return <span>{key === 'contract_value' ? money(l.contract_value) : (val || '—')}</span>;
    return (
      <input
        className={`cell ${cls}`}
        style={w ? { width: w } : undefined}
        value={val}
        aria-label={`${l.code ?? ''} ${String(key).replace('_', ' ')}`}
        inputMode={key === 'description' || key === 'code' || key === 'note' ? undefined : 'decimal'}
        onFocus={() => draft[k] === undefined && setDraft({ ...draft, [k]: raw })}
        onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
        onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
        onBlur={() => {
          if (draft[k] === undefined) return;
          const v = draft[k];
          setDraft(({ [k]: _drop, ...rest }) => rest);
          if (v !== raw) void save(l, key, v);
        }}
      />
    );
  };

  return (
    <div>
      <div className="lines-bar">
        <button type="button" className={`chip ${onlyDiff ? 'on' : ''}`} aria-pressed={onlyDiff} onClick={() => setOnlyDiff(!onlyDiff)}>
          Only lines with a difference
        </button>
        <span className="grow" />
        {canEdit ? (
          <>
            {previousLines && previousLines.lines.length ? (
              <Button
                kind="ghost"
                disabled={Boolean(busy)}
                onClick={() =>
                  void addMany(
                    previousLines.lines.map((l) => ({ ...l, prev_pct: l.swi_pct ?? l.claimed_pct, claimed_pct: null, swi_pct: null })),
                    'carried',
                    `Lines carried from ${previousLines.label} (previous % = SWI's agreed %)`,
                  )
                }
              >
                Start from {previousLines.label}
              </Button>
            ) : null}
            <Button kind="ghost" onClick={() => setPaste(paste === null ? '' : null)}>
              Paste from Excel
            </Button>
            <Button kind="ghost" onClick={() => void addMany([{ description: 'New line', contract_value: null }], 'manual', 'Added a claim line')}>
              + Line
            </Button>
          </>
        ) : null}
      </div>
      {paste !== null ? (
        <div className="paste-box">
          <label className="field">
            <span className="field-label">Copy the rows in Excel (code, description, contract value, previous %, claimed %) and paste here</span>
            <textarea rows={6} value={paste} onChange={(e) => setPaste(e.target.value)} placeholder={'4.10\tRoof sheeting\t640,000.00\t40%\t85%'} />
          </label>
          <div className="resp-row">
            <span className="muted small">{paste ? `${parsePaste(paste).length} lines found` : ''}</span>
            <label className="check small">
              <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} /> Replace the lines already here
            </label>
            <Button kind="primary" disabled={!paste || !parsePaste(paste).length || Boolean(busy)} onClick={() => void addMany(parsePaste(paste), 'paste', 'Claim lines pasted from Excel')}>
              {busy ?? 'Add lines'}
            </Button>
            <Button onClick={() => setPaste(null)}>Cancel</Button>
          </div>
        </div>
      ) : null}
      {busy && paste === null ? <div className="banner banner-info">{busy}</div> : null}
      {lines.length === 0 ? (
        <p className="muted small" style={{ padding: '4px 0' }}>
          No lines yet. Let SWI AI read the claim PDF, paste the Excel breakdown, {previousLines?.lines.length ? `start from ${previousLines.label}, ` : ''}or add lines one by one. Without lines, type the works total in the claim details.
        </p>
      ) : (
        <div className="table-wrap">
          <table className="t lines">
            <thead>
              <tr>
                <th>Item</th>
                <th>Description</th>
                <th className="right">Contract value</th>
                <th className="right">Prev %</th>
                <th className="right">Claimed %</th>
                <th className="right">SWI %</th>
                <th className="right">Claimed now</th>
                <th className="right">SWI agrees</th>
                <th className="right">Difference</th>
                <th>Note</th>
                {canEdit ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {shown.map(({ l, a }) => {
                const d = a.claimed - a.swi;
                return (
                  <tr key={l.id} className={Math.abs(d) > 0.005 ? 'flag' : ''}>
                    <td className="num">{cell(l, 'code', '', 56)}</td>
                    <td>{cell(l, 'description', 'wide')}</td>
                    <td className="right">{cell(l, 'contract_value', 'r', 110)}</td>
                    <td className="right">{cell(l, 'prev_pct', 'r', 56)}</td>
                    <td className="right">{cell(l, 'claimed_pct', 'r', 56)}</td>
                    <td className="right strong">{cell(l, 'swi_pct', 'r swi', 56)}</td>
                    <td className="right">{money(a.claimed)}</td>
                    <td className="right">{money(a.swi)}</td>
                    <td className={`right ${Math.abs(d) > 0.005 ? 'bad-t' : ''}`}>{Math.abs(d) > 0.005 ? `− ${money(d)}` : '—'}</td>
                    <td>{cell(l, 'note', 'wide')}</td>
                    {canEdit ? (
                      <td>
                        <button type="button" className="linkbtn danger small" aria-label={`Remove line ${l.code ?? ''}`} onClick={() => void remove(l)}>
                          ✕
                        </button>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
              <tr className="tot">
                <td colSpan={6}>Totals ({lines.length} lines)</td>
                <td className="right">{money(tc)}</td>
                <td className="right">{money(ts)}</td>
                <td className="right">{tc - ts > 0.005 ? `− ${money(tc - ts)}` : '—'}</td>
                <td colSpan={canEdit ? 2 : 1} />
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
